import { ButtonGroup, Fab, FormControlLabel, FormGroup, Grid, IconButton, Paper, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip } from "@mui/material"
import { MinimalStore } from "../../apis/ersys-data/api"
import { AddSharp, CancelSharp, CheckCircleSharp, RemoveSharp } from "@mui/icons-material"
import { useEffect, useState } from "react"
import { useDebounce } from "../useDebounce"
import { useErsysDataApi } from "../ErsysDataApiProvider"
import { useFirebase } from "../FirebaseProvider"
import { useDispatch } from "react-redux"
import { showError } from "../../features/errors/Errors"
import { setIsLoading } from "../../features/menus/AppMenu"

interface PaymentPlansStoresTabProps {
    ids: number[],
    showSkeleton: () => boolean
    onChange: (icdls: number[]) => void
}

const PaymentPlansStoresTab: React.FC<PaymentPlansStoresTabProps> = ({ ids, showSkeleton, onChange }) => {
    const [filter, setFilter] = useState<string>("")
    const [stores, setStores] = useState<MinimalStore[]>([])
    const [activeLookup, setActiveLookup] = useState<Record<number, boolean>>({})
    const [visibleStores, setVisibleStores] = useState<MinimalStore[]>([])
    const debouncedFilter: string = useDebounce(filter, 500)
    const ersysDataApiServices = useErsysDataApi()
    const { isReady } = useFirebase()
    const dispatch = useDispatch()

    useEffect(() => {
        if (debouncedFilter === "") {
            setVisibleStores(stores.slice(0, 50))
        }
        setVisibleStores(stores.filter(i => 
            i.fullName.toLocaleLowerCase().includes(debouncedFilter.toLocaleLowerCase()) || 
            i.prefix.toLocaleLowerCase().includes(debouncedFilter.toLocaleLowerCase())
        ).slice(0, 50))
    }, [stores, debouncedFilter, setVisibleStores])

    useEffect(() => {
        const set: Record<number, boolean> = {}
        for(const id of ids) {
            set[id] = true
        }
        setActiveLookup(set)
    }, [ids, setActiveLookup])

    useEffect(() => {
        if(!isReady) {
            return
        }
        dispatch(setIsLoading(true))
        const fetch = async () => {
            try {
                const resp = await ersysDataApiServices.storesService.storesList()
                setStores(resp.data.stores)
            }
            catch (ex: any) {
                dispatch(
                    showError({
                        title: "Unexpected Error occurred",
                        message:
                            "An unexpected error occurred while getting store data. If problem persists please contact IT.",
                    }),
                )
                console.log(ex)
            } finally {
                dispatch(setIsLoading(false))
            }
        }
        fetch()

    }, [setStores, ersysDataApiServices.storesService, dispatch, isReady])

    const localRenderSkeleton = () => {
        return visibleStores.length < 1 && debouncedFilter !== ''
    }

    const onAddIc = (id: number) => {
        onChange([...ids, id])
    }

    const onRemoveIc = (id: number) => {
        onChange([...ids].filter(i => i !== id))
    }

    const addAll = () => {
        onChange([...stores.map(x => x.id)])
    }

    const removeAll = () => {
        onChange([])
    }

    const canRemoveAll = () => {
        return ids.length === stores.length
    }

    const renderSkeleton = () => {
        return (
            <FormGroup>
                {[...Array(5)].map((_, i) => (
                    <FormControlLabel
                        control={<Skeleton variant='rectangular' width={20} height={20} sx={{ marginRight: 2 }} />}
                        key={`skele-${i}`}
                        label={<Skeleton variant='rectangular' width={200} height={20} />}
                        sx={{ margin: 1.5 }} />
                )
                )}
            </FormGroup>
        )
    }

    return (
        <Grid container spacing={2}>
            { !canRemoveAll() && <Tooltip title="Add All">
                <Fab
                    onClick={() => addAll()}
                    color="primary"
                    aria-label="add"
                    sx={{ position: 'absolute', bottom: 25, right: 25 }}>
                    <AddSharp />
                </Fab>
            </Tooltip> }
            { canRemoveAll() && <Tooltip title="Remove All">
                <Fab
                    onClick={() => removeAll()}
                    color="primary"
                    aria-label="add"
                    sx={{ position: 'absolute', bottom: 25, right: 25 }}>
                    <RemoveSharp />
                </Fab>
            </Tooltip> }
            <Grid xs={6} item>
                {showSkeleton() || localRenderSkeleton() ? renderSkeleton() :
                    (
                        <TableContainer sx={{ minWidth: 650, maxHeight: 550 }} component={Paper}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Prefix</TableCell>
                                        <TableCell>Name</TableCell>
                                        <TableCell align="right">
                                            <TextField
                                                size="small"
                                                value={filter}
                                                placeholder='Search'
                                                onChange={(e) => setFilter(e.target.value)} />
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {visibleStores.map((store) => (
                                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }} key={store.id}>
                                            <TableCell>{store.prefix}</TableCell>
                                            <TableCell>{store.fullName}</TableCell>
                                            <TableCell align="right">
                                                <ButtonGroup>
                                                    {activeLookup[store.id] &&
                                                        <Tooltip title="Is Included">
                                                            <IconButton onClick={() => onRemoveIc(store.id)} color="success">
                                                                <CheckCircleSharp></CheckCircleSharp>
                                                            </IconButton>
                                                        </Tooltip>
                                                    }
                                                    {!activeLookup[store.id] &&
                                                        <Tooltip title="Is Not Included">
                                                            <IconButton onClick={() => onAddIc(store.id)} color="error">
                                                                <CancelSharp></CancelSharp>
                                                            </IconButton>
                                                        </Tooltip>
                                                    }
                                                </ButtonGroup>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
            </Grid>

            <Grid xs={6} item>
                {showSkeleton() ? renderSkeleton() :
                    (<FormGroup>
                    </FormGroup>)}
            </Grid>
        </Grid>
    )
}

export default PaymentPlansStoresTab